class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.loadProductFallback());
    } else {
      this.loadProductFallback();
    }
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }

  async loadProductFallback() {
    const fallback = document.getElementById('search-products-fallback');
    const resultsList = document.querySelector('[data-search-results-list]');
    if (!fallback || !resultsList) return;

    const query = fallback.dataset.searchQuery?.trim();
    if (!query) return;

    if (resultsList.querySelector('.product-card-wrapper')) return;

    const catalogPending = fallback.dataset.catalogPending === 'true';
    if (catalogPending) {
      this.showLoader();
    }

    try {
      let products = await this.fetchSuggestProducts(query);

      if (!products.length) {
        products = await this.fetchCatalogProducts(query);
      }

      if (!products.length) {
        if (catalogPending) this.showNoResults();
        return;
      }

      const handles = products
        .slice(0, 24)
        .map((product) => product.handle)
        .filter(Boolean);

      if (!handles.length) {
        if (catalogPending) this.showNoResults();
        return;
      }

      const rendered = await this.renderProductSection(handles);
      if (!rendered) {
        if (catalogPending) this.showNoResults();
        return;
      }

      this.clearEmptyState();
    } catch (error) {
      console.error('Search product fallback failed:', error);
      if (catalogPending) this.showNoResults();
    } finally {
      if (catalogPending) {
        this.hideLoader();
      }
    }
  }

  showLoader() {
    const grid = document.getElementById('product-grid');
    grid?.classList.add('loading', 'search-catalog-loading');
    document.querySelector('[data-search-no-results]')?.classList.add('hidden');
    document.querySelector('.template-search')?.classList.remove('template-search--empty');
    document.querySelector('.template-search')?.classList.add('template-search--catalog-loading');
  }

  hideLoader() {
    const grid = document.getElementById('product-grid');
    grid?.classList.remove('loading', 'search-catalog-loading');
  }

  showNoResults() {
    const noResults = document.querySelector('[data-search-no-results]');
    if (noResults) noResults.classList.remove('hidden');
    document.querySelector('.template-search')?.classList.remove('template-search--catalog-loading');
    document.querySelector('.template-search')?.classList.add('template-search--empty');
  }

  async fetchSuggestProducts(query) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '24',
      'resources[options[unavailable_products]]': 'last',
    });

    const response = await fetch(`/search/suggest.json?${params}`);
    if (!response.ok) return [];

    const data = await response.json();
    return data?.resources?.results?.products || [];
  }

  async fetchCatalogProducts(query) {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return [];

    const catalog = [];

    for (let page = 1; page <= 12; page += 1) {
      const response = await fetch(`/products.json?limit=250&page=${page}`);
      if (!response.ok) break;

      const data = await response.json();
      const batch = data?.products || [];
      if (!batch.length) break;

      catalog.push(...batch);
      if (batch.length < 250) break;
    }

    return catalog.filter((product) => {
      const haystack = [
        product.title,
        product.handle,
        product.product_type,
        product.vendor,
        ...(product.tags || []),
      ]
        .join(' ')
        .toLowerCase();

      return terms.every((term) => haystack.includes(term));
    });
  }

  async renderProductSection(handles) {
    const resultsList = document.querySelector('[data-search-results-list]');
    if (!resultsList) return false;

    const sectionResponse = await fetch(
      `/?sections=rocket-search-products&handles=${encodeURIComponent(handles.join(','))}`
    );
    if (!sectionResponse.ok) return false;

    const sectionData = await sectionResponse.json();
    const html = sectionData['rocket-search-products'];
    if (!html) return false;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const items = parsed.querySelectorAll('.grid__item');
    if (!items.length) return false;

    items.forEach((item) => resultsList.append(item));
    return true;
  }

  clearEmptyState() {
    document.querySelector('.template-search')?.classList.remove('template-search--empty');
    document.querySelector('.template-search')?.classList.remove('template-search--catalog-loading');
    document.querySelector('[data-search-no-results]')?.remove();
  }
}

customElements.define('main-search', MainSearch);
